import React, { useState, useRef, useEffect } from 'react';

interface EditableFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  multiline?: boolean;
  disabled?: boolean;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onChange,
  placeholder = 'Click to edit',
  className = '',
  multiline = false,
  disabled = false
}) => {
  // Strip HTML tags for display
  const stripHtml = (html: string) => {
    if (!html) return '';
    // Remove HTML tags using regex and decode HTML entities
    return html
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with "
      .trim();
  };

  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(stripHtml(value));
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update tempValue when value prop changes
  useEffect(() => {
    setTempValue(stripHtml(value));
  }, [value]);

  const handleSave = () => {
    console.log('🔍 EditableField handleSave - tempValue:', tempValue);
    console.log('🔍 EditableField handleSave - value prop:', value);
    onChange(tempValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(stripHtml(value));
    setIsEditing(false);
  };

  if (isEditing) {
    const Component = multiline ? 'textarea' : 'input';
    return (
      <Component
        ref={inputRef as any}
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !multiline) handleSave();
          if (e.key === 'Escape') handleCancel();
        }}
        className={`border border-blue-500 ring-2 ring-blue-200 rounded px-2 py-1 focus:outline-none ${className}`}
        rows={multiline ? 3 : undefined}
      />
    );
  }

  return (
    <div
      onClick={() => !disabled && setIsEditing(true)}
      className={`${disabled ? 'cursor-not-allowed bg-gray-100' : 'cursor-pointer hover:bg-gray-50'} rounded px-2 py-1 min-h-[2rem] ${
        !value ? 'text-gray-400' : ''
      } ${className}`}
    >
      {value ? stripHtml(value) : placeholder}
    </div>
  );
};
